import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { deleteCookie } from 'cookies-next';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { ICard, IOptionSelectFields } from '../../../types/contentful';

import { useSubscriptionsContext } from '../../context/Subscriptions';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getCookieDomain } from '../../utils/cookies';

import { BlockContainer, Column, Layout } from '../Layout';
import { Text } from '../Text';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';

import { InlineAnchor } from '../RichText/styles';
import { SubscriptionButton, OptionContainer, Details } from './styles';

export function OptionSelect({ options, disclaimer }: IOptionSelectFields) {
  const router = useRouter();
  const stateMap = options.reduce(
    (acc, { fields }) => ({ ...acc, [fields.id]: false }),
    {}
  );
  const [routeLoading, setRouteLoading] =
    useState<Record<string, boolean>>(stateMap);

  const { setPayment } = useSubscriptionsContext();

  useEffect(() => {
    deleteCookie('gl_purchase_subscription_external_id', {
      domain: getCookieDomain()
    });
  }, []);

  return (
    <BlockContainer
      marginTop={{ 0: 'blockMarginY', desktop: 'blockMarginY_tablet' }}
      marginBottom={{
        0: 'blockMarginY',
        desktop: 'blockMarginY_tablet'
      }}
      data-testid="subscription-options"
    >
      <Layout
        gutter={{ 0: 0, tablet: 'ml' }}
        flexDirection={{
          0: 'column',
          tablet: 'row'
        }}
      >
        {options.map(({ fields }: ICard) => {
          const {
            id,
            title,
            details,
            ctaUrl = '/purchase-subscriptions/customize'
          } = fields ?? {};
          return (
            <Column
              key={title}
              size={{ min: 12, tablet: 6, desktop: 3 }}
              padding={{ 0: 's', desktop: ['m', 'sm'] }}
              style={{ height: 'fit-content' }}
            >
              <OptionContainer flexDirection="column">
                <Text
                  fontSize={{ 0: '2xl', tablet: 'xl' }}
                  fontWeight="500"
                  lineHeight="l"
                  marginBottom="s"
                >
                  {title}
                </Text>

                <Details>
                  <RichText
                    data={details}
                    overrideOptions={{
                      [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                        <Text
                          as="p"
                          fontWeight="p"
                          fontSize={{ 0: 'sm', desktop: 'm' }}
                          lineHeight={{ 0: 'sm', desktop: 'ml' }}
                          textAlign="left"
                          colour="secondaryText"
                        >
                          {children}
                        </Text>
                      )
                    }}
                  />
                </Details>

                <SubscriptionButton
                  onClick={() => {
                    setRouteLoading({ ...stateMap, [id]: true });
                    const amount = parseInt(id, 10);
                    trackWithFingerprint('CTA Clicked', {
                      page_url: window.location.href,
                      module: 'gifting_option',
                      amount
                    });
                    setPayment?.(amount);
                    router.push(`${ctaUrl}?selectedPlan=${id}`);
                  }}
                  data-testid={`option-select-button-${id}`}
                >
                  {!!routeLoading[id as string] ? 'Loading...' : 'Select'}
                </SubscriptionButton>
              </OptionContainer>
            </Column>
          );
        })}

        <Column size={12} padding={{ 0: [0, 's'] }}>
          <RichText
            data={disclaimer}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize={{ 0: 's', desktop: 'm' }}
                  lineHeight={{ 0: 'smm', desktop: 'ml' }}
                  textAlign="center"
                  colour="secondaryText"
                >
                  {children}
                </Text>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <InlineAnchor href={node.data.uri} colour="secondaryText">
                  {children}
                </InlineAnchor>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="secondaryText"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
        </Column>
      </Layout>
    </BlockContainer>
  );
}
